import { LocationDot, LocationDotStatus } from '@/components/LocationDotsTile/LocationDotsBuilding.vue';
import { GetBuildingsForLocationDotsDocument, LocationDotsBuildingFragment } from '@/generated/graphql';
import { useQuery } from '@vue/apollo-composable';
import { computed, Ref, toValue } from 'vue';

export function useEventLocationDotsData(variantId: Ref<string>) {
    const { result, loading } = useQuery(
        GetBuildingsForLocationDotsDocument,
        () => ({
            roomType: 'Hallenausstellungsfläche',
            levelType: 'Hallenausstellungsebene',
            eventVariantId: toValue(variantId),
        }),
        () => ({
            enabled: !!toValue(variantId),
        }),
    );

    function getLocationDotStatus(allocationCount: number, optionalAllocationCount: number): LocationDotStatus {
        if (allocationCount > 0 && optionalAllocationCount === 0) {
            return 'Occupied';
        }
        if (optionalAllocationCount > 0 && allocationCount === 0) {
            return 'Optionally-Occupied';
        }
        return 'Empty';
    }

    const locations = computed(() => result.value?.buildings?.nodes ?? []);
    const levels = computed(() => locations.value.flatMap((b) => b.levels.nodes));
    const rooms = computed(() => levels.value.flatMap((l) => l.rooms.nodes));

    const CHUNK_SIZE = 6;

    function createBuildingChunks() {
        const buildingChunks = [];
        for (let i = 0; i < locations.value.length; i = i + CHUNK_SIZE) {
            buildingChunks.push(locations.value.slice(i, i + CHUNK_SIZE));
        }
        return buildingChunks;
    }

    const buildingChunks = computed(() => createBuildingChunks());

    function mapBuildingData(
        data: LocationDotsBuildingFragment[],
        defaultTooltipPlacement: 'top' | 'bottom',
        minElevation: number,
        maxElevation: number,
    ) {
        return data.map((b) => {
            const levels: LocationDot[] = [];
            const levelsWithElevation = b.levels.nodes.filter((l) => l.elevation);
            const extistingElevations = levelsWithElevation.flatMap((l) => parseInt(l.elevation?.value));
            for (let elevation = minElevation; elevation <= maxElevation; elevation++) {
                if (extistingElevations.includes(elevation)) {
                    const matchingLevel = levelsWithElevation.find((l) => parseInt(l.elevation?.value) === elevation);
                    if (matchingLevel) {
                        const allocationsCount = matchingLevel.rooms.nodes.reduce(
                            (acc, value) => acc + value.allocationRooms.totalCount,
                            0,
                        );
                        const optionalAllocationsCount = matchingLevel.rooms.nodes.reduce(
                            (acc, value) => acc + value.optionalAllocationRooms.totalCount,
                            0,
                        );
                        const elevationValue = matchingLevel.elevation?.value;
                        const displayLevel =
                            elevationValue?.endsWith('.0') || elevationValue?.endsWith('.00')
                                ? Number(elevationValue.slice(0, -2))
                                : Number(elevationValue);
                        levels.push({
                            status: getLocationDotStatus(allocationsCount, optionalAllocationsCount),
                            level: displayLevel,
                        });
                    }
                } else {
                    levels.push({ status: 'Placeholder', level: elevation });
                }
            }
            return {
                id: b.id,
                buildingName: b.nameShort,
                occupiedExhibitionAreaCount: levelsWithElevation.length,
                defaultTooltipPlacement: defaultTooltipPlacement,
                levels: levels.sort((a, b) => b.level - a.level),
            };
        });
    }

    function mapBuildingChunksData() {
        const mappedBuildingChunks = [];
        for (const [index, chunk] of buildingChunks.value.entries()) {
            const elevations = chunk.flatMap((b) =>
                b.levels.nodes.filter((L) => L.elevation).map((l) => parseInt(l.elevation?.value)),
            );
            const minElevation = Math.min(...elevations);
            const maxElevation = Math.max(...elevations);
            // The last row should have the tooltips at the bottom
            mappedBuildingChunks.push(
                mapBuildingData(
                    chunk,
                    index < buildingChunks.value.length - 1 ? 'top' : 'bottom',
                    minElevation,
                    maxElevation,
                ),
            );
        }
        return mappedBuildingChunks;
    }

    const mappedBuildingChunks = computed(() => mapBuildingChunksData());

    const sumOccupied = computed(() =>
        rooms.value
            .filter((r) => r.allocationRooms.totalCount > 0)
            .reduce((a, b) => a + (parseFloat(b.roomCadGrossSpaceMins.nodes?.[0].value) ?? 0.0), 0.0),
    );
    const sumOptional = computed(() =>
        rooms.value
            .filter((r) => r.optionalAllocationRooms.totalCount > 0)
            .reduce((a, b) => a + (parseFloat(b.roomCadGrossSpaceMins.nodes?.[0].value) ?? 0.0), 0.0),
    );

    const sumTotal = computed(() => sumOccupied.value + sumOptional.value);

    const countOccupied = computed(() => rooms.value.filter((room) => room.allocationRooms.totalCount > 0).length);
    const countOptional = computed(
        () => rooms.value.filter((room) => room.optionalAllocationRooms.totalCount > 0).length,
    );
    const countTotal = computed(() => countOccupied.value + countOptional.value);

    return {
        loading,
        locations,
        sumOccupied,
        sumOptional,
        sumTotal,
        countOccupied,
        countOptional,
        countTotal,
        mappedBuildingChunks,
    };
}
